import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { DemoModeContext } from "@/providers/demo-mode-context";

// STORAGE_KEY intentionally kept as "demoMode" (renamed to "Modo Privacidad"
// in the UI in US-21, but migrating the key would reset the owner's
// remembered preference). Internal API names (useDemoMode, demoMode,
// data-demo) also stay for the same reason — UI copy only.
const STORAGE_KEY = "demoMode";

function readInitialDemoMode(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

/**
 * DemoModeProvider — toggles `<html data-demo>` + persists in localStorage.
 *
 * IMPORTANT: masking is strictly client-side. Network requests / TanStack
 * Query cache / Supabase responses always contain real data. Only the
 * `<Amount>`/`<MaskedXxx>` presentational components consult this context
 * to render obfuscated text.
 */
export function DemoModeProvider({ children }: { children: ReactNode }) {
  const [demoMode, setDemoModeState] = useState<boolean>(() => readInitialDemoMode());

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (demoMode) {
      document.documentElement.dataset.demo = "true";
    } else {
      delete document.documentElement.dataset.demo;
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, demoMode ? "true" : "false");
    } catch {
      /* swallow */
    }
  }, [demoMode]);

  const setDemoMode = useCallback((next: boolean) => {
    setDemoModeState(next);
  }, []);

  const toggleDemoMode = useCallback(() => {
    setDemoModeState((prev) => !prev);
  }, []);

  const value = useMemo(
    () => ({ demoMode, setDemoMode, toggleDemoMode }),
    [demoMode, setDemoMode, toggleDemoMode],
  );

  return <DemoModeContext.Provider value={value}>{children}</DemoModeContext.Provider>;
}
